/**
 * <mb-rescate>: oferta de rescate (docs/diseno/oferta-rescate.md).
 *
 * - Señales: intención de salida (ordenador), vuelta a la pestaña, duda en ficha, carrito parado y scroll rápido
 *   hacia arriba (móvil). Nunca toca el historial ni el botón "atrás".
 * - Reglas: grupo de control estable, una vez cada 30 días, sin código vivo, sin compra reciente, ≥ 20 s en la web,
 *   una sola vez por sesión y solo en ficha, colección o carrito.
 * - Oferta: nombre gratis (tarros), empujón al primer tramo (sin descuento) o 5 % general.
 * - Guarda el estado en localStorage solo si el cliente permite cookies de preferencias; si no, en sessionStorage.
 * - Prueba (solo con el ajuste mb_rescate_demo): ?rescate=forzar | forzar-tarro | forzar-general | forzar-empujon.
 *
 * Eventos: window 'mb:rescate' (detail: { accion: 'visto' | 'aceptado' | 'cerrado', tipo }) y, si existe,
 * Shopify.analytics.publish('rescate_visto' | 'rescate_aceptado' | 'rescate_cerrado', { tipo }).
 */

const CLAVE = 'mb:rescate';
const CLAVE_T0 = 'mb:rescate:t0';
const CLAVE_SESION = 'mb:rescate:ofrecida';
const DIA_MS = 86_400_000;
/** Validez del cupón flash (debe coincidir con MINUTOS_VALIDEZ del PIM). */
const VALIDEZ_MS = 49 * 60_000;
const TREINTA_DIAS_MS = 30 * DIA_MS;
const ZONA = 'Europe/Madrid';
const JSON_CABECERAS = { 'Content-Type': 'application/json', Accept: 'application/json' };

const TEXTOS = {
  tarro: {
    titulo: '¿Le ponemos su nombre gratis?',
    texto: 'Te regalamos la personalización de tu tarro si lo pides en los próximos 49 minutos.',
    boton: 'Quiero el nombre gratis',
    barra: 'Tu nombre gratis está aplicado',
  },
  general: {
    titulo: 'Un 5 % para que te decidas',
    texto: 'En pedidos desde 15 €. Caduca en 49 minutos.',
    boton: 'Aplicar mi 5 %',
    barra: 'Tu 5 % está aplicado',
  },
  error: 'No hemos podido generar tu código. Inténtalo más tarde.',
  errorBolsa: 'No hemos podido añadir la bolsa. Inténtalo de nuevo desde el carrito.',
  enviando: 'Un momento…',
};

const raiz = () => window.Shopify?.routes?.root ?? '/';

/** @returns {boolean} si el cliente permite guardar preferencias en el navegador */
function permitePreferencias() {
  try {
    return Boolean(window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.());
  } catch {
    return false;
  }
}

/**
 * @param {'localStorage' | 'sessionStorage'} almacen
 * @returns {Record<string, any>}
 */
function leerAlmacen(almacen) {
  try {
    const bruto = window[almacen].getItem(CLAVE);
    return bruto ? JSON.parse(bruto) : {};
  } catch {
    return {};
  }
}

/** @returns {Record<string, any>} estado guardado (el de localStorage manda sobre el de sessionStorage) */
function leerEstado() {
  return { ...leerAlmacen('sessionStorage'), ...leerAlmacen('localStorage') };
}

/** @param {Record<string, any>} parcial */
function guardarEstado(parcial) {
  const siguiente = { ...leerEstado(), ...parcial };
  const almacen = permitePreferencias() ? 'localStorage' : 'sessionStorage';
  try {
    window[almacen].setItem(CLAVE, JSON.stringify(siguiente));
  } catch {
    /* almacenamiento bloqueado: la oferta sigue funcionando en esta página */
  }
}

function uuid() {
  if (crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** @param {string} iso */
function formatearCaducidad(iso) {
  const fecha = new Date(iso);
  const dia = (/** @type {Date} */ f) =>
    new Intl.DateTimeFormat('es-ES', { timeZone: ZONA, year: 'numeric', month: '2-digit', day: '2-digit' }).format(f);
  const hora = new Intl.DateTimeFormat('es-ES', { timeZone: ZONA, hour: '2-digit', minute: '2-digit', hour12: false }).format(fecha);
  if (dia(fecha) === dia(new Date())) return `hoy a las ${hora}`;
  if (dia(fecha) === dia(new Date(Date.now() + DIA_MS))) return `mañana a las ${hora}`;
  const nombre = new Intl.DateTimeFormat('es-ES', { timeZone: ZONA, weekday: 'long', day: 'numeric' }).format(fecha);
  return `el ${nombre} a las ${hora}`;
}

class MbRescate extends HTMLElement {
  /** @type {AbortController | null} */
  #controlador = null;
  /** @type {HTMLDialogElement} */
  #dialogo;
  /** @type {'tarro' | 'general' | 'empujon'} */
  #tipo = 'general';
  #aceptado = false;
  #ocupado = false;
  #interaccion = false;
  #ultimaActividad = Date.now();
  #idleDisparado = false;
  #segundosVisibles = 0;
  #salidaDisparada = false;
  /** @type {number | undefined} */
  #temporizadorCaducidad;
  /** @type {MutationObserver | null} */
  #observador = null;

  connectedCallback() {
    this.#controlador = new AbortController();
    const { signal } = this.#controlador;

    this.#dialogo = /** @type {HTMLDialogElement} */ (this.querySelector('[data-dialogo]'));
    if (!this.#dialogo) return;

    try {
      window.Shopify?.loadFeatures?.([{ name: 'consent-tracking-api', version: '0.1' }], () => {});
    } catch {
      /* sin API de privacidad: se usa sessionStorage */
    }

    this.#registrarSesion();
    this.#detectarCompra();
    this.#limpiarCaducado();

    // Barra y línea del carrito: en todas las páginas mientras el código esté vivo.
    const barra = this.querySelector('[data-barra]');
    if (barra) document.body.prepend(barra);
    this.#pintarOferta();
    let pendiente = false;
    this.#observador = new MutationObserver(() => {
      if (pendiente) return;
      pendiente = true;
      requestAnimationFrame(() => {
        pendiente = false;
        this.#pintarLinea();
      });
    });
    this.#observador.observe(document.body, { childList: true, subtree: true });

    this.querySelector('[data-barra-cerrar]')?.addEventListener(
      'click',
      () => {
        guardarEstado({ barraCerrada: true });
        this.#pintarBarra();
      },
      { signal }
    );

    this.#dialogo.addEventListener('click', (e) => e.target === this.#dialogo && this.#dialogo.close(), { signal });
    this.#dialogo.addEventListener('close', this.#alCerrar, { signal });
    this.querySelector('[data-cerrar]')?.addEventListener('click', () => this.#dialogo.close(), { signal });
    this.querySelector('[data-form]')?.addEventListener('submit', this.#alAceptar, { signal });
    this.#activarDeslizar(signal);

    const forzado = this.#modoForzado();
    if (forzado) {
      setTimeout(() => this.#mostrar(forzado), 700);
      return;
    }
    if (this.dataset.puedeOfrecer !== 'true' || !this.#cumpleReglasFijas()) return;
    this.#activarSenales(signal);
  }

  disconnectedCallback() {
    this.#controlador?.abort();
    this.#observador?.disconnect();
    clearTimeout(this.#temporizadorCaducidad);
  }

  /* ------------------------------------------------------------------ estado y reglas */

  /** Marca el inicio de la sesión (para el mínimo de 20 s en la web) y crea el identificador del navegador. */
  #registrarSesion() {
    try {
      if (!sessionStorage.getItem(CLAVE_T0)) sessionStorage.setItem(CLAVE_T0, String(Date.now()));
    } catch {
      /* se usa el momento de carga de esta página */
    }
    const estado = leerEstado();
    const nuevo = {};
    if (!estado.id) nuevo.id = uuid();
    if (typeof estado.grupo !== 'number') nuevo.grupo = Math.random();
    if (Object.keys(nuevo).length) guardarEstado(nuevo);
  }

  /** @returns {number} milisegundos desde que empezó la sesión */
  #tiempoEnWeb() {
    try {
      const inicio = Number(sessionStorage.getItem(CLAVE_T0));
      if (inicio) return Date.now() - inicio;
    } catch {
      /* ver arriba */
    }
    return performance.now();
  }

  /** Si venimos de la página de gracias, anota la compra y descarta el código (ya usado). */
  #detectarCompra() {
    const gracias = /thank[_-]?you|\/orders\//i;
    if (gracias.test(location.pathname) || gracias.test(document.referrer)) {
      guardarEstado({ compra: Date.now(), codigo: null, caduca: null });
    }
  }

  #limpiarCaducado() {
    const { caduca } = leerEstado();
    if (caduca && new Date(caduca).getTime() <= Date.now()) guardarEstado({ codigo: null, caduca: null });
  }

  #codigoVivo() {
    const { codigo, caduca } = leerEstado();
    return Boolean(codigo && caduca && new Date(caduca).getTime() > Date.now());
  }

  /** Reglas que no cambian durante la visita: control, visto, código vivo, compra reciente. */
  #cumpleReglasFijas() {
    const estado = leerEstado();
    const control = Number(this.dataset.control) || 0;
    if (estado.grupo * 100 < control) return false;
    if (estado.visto && Date.now() - estado.visto < TREINTA_DIAS_MS) return false;
    if (estado.compra && Date.now() - estado.compra < TREINTA_DIAS_MS) return false;
    return !this.#codigoVivo();
  }

  #ofrecidaEnSesion() {
    try {
      return sessionStorage.getItem(CLAVE_SESION) === '1';
    } catch {
      return Boolean(this.#aceptado || this.#dialogo.dataset.visto);
    }
  }

  /** @returns {'forzar' | 'tarro' | 'general' | 'empujon' | null} */
  #modoForzado() {
    if (this.dataset.demo !== 'true') return null;
    const valor = new URLSearchParams(location.search).get('rescate');
    if (!valor?.startsWith('forzar')) return null;
    const tipo = valor.replace('forzar-', '');
    return tipo === 'tarro' || tipo === 'general' || tipo === 'empujon' ? tipo : 'forzar';
  }

  /* ------------------------------------------------------------------ señales */

  /** @param {AbortSignal} signal */
  #activarSenales(signal) {
    const opciones = { signal, passive: true };
    const raton = matchMedia('(hover: hover) and (pointer: fine)').matches;
    const tactil = matchMedia('(pointer: coarse)').matches || innerWidth < 750;

    // Actividad: sirve para "duda en ficha" y para "carrito parado".
    const actividad = () => {
      this.#ultimaActividad = Date.now();
      this.#idleDisparado = false;
    };
    for (const nombre of ['pointermove', 'pointerdown', 'keydown', 'touchstart']) {
      document.addEventListener(nombre, actividad, opciones);
    }
    const interaccion = () => {
      this.#interaccion = true;
    };
    for (const nombre of ['scroll', 'pointerdown', 'keydown', 'touchstart']) {
      document.addEventListener(nombre, interaccion, opciones);
    }

    // 1. Intención de salida (ordenador): el ratón sale por arriba de la ventana.
    if (raton) {
      document.addEventListener(
        'mouseout',
        (e) => {
          if (e.relatedTarget || e.clientY > 0 || this.#salidaDisparada) return;
          this.#salidaDisparada = true;
          this.#intentar();
        },
        opciones
      );
    }

    // 2. Vuelta a la pestaña tras ≥ 30 s fuera.
    let ocultaDesde = 0;
    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.visibilityState === 'hidden') {
          ocultaDesde = Date.now();
          return;
        }
        if (ocultaDesde && Date.now() - ocultaDesde >= 30_000) this.#intentar();
        ocultaDesde = 0;
      },
      { signal }
    );

    // 3. Scroll rápido hacia arriba (móvil), tras haber bajado más de una pantalla.
    if (tactil) {
      let maximo = scrollY;
      let ref = { y: scrollY, t: performance.now() };
      document.addEventListener(
        'scroll',
        () => {
          const y = scrollY;
          const t = performance.now();
          maximo = Math.max(maximo, y);
          if (t - ref.t < 100) return;
          const velocidad = ((ref.y - y) / (t - ref.t)) * 1000;
          ref = { y, t };
          if (velocidad > 2000 && maximo > innerHeight) this.#intentar();
        },
        opciones
      );
    }

    // 4 y 5. Duda en ficha y carrito parado: un reloj de 5 s que solo cuenta con la pestaña visible.
    const reloj = setInterval(() => this.#alTic(), 5000);
    signal.addEventListener('abort', () => clearInterval(reloj));
  }

  async #alTic() {
    if (document.visibilityState !== 'visible' || this.#dialogo.open) return;
    this.#segundosVisibles += 5;

    // Duda en ficha: ≥ 75 s con actividad y sin haber añadido ese producto al carrito.
    if (this.dataset.plantilla === 'product' && this.#segundosVisibles === 75 && this.#interaccion) {
      const carrito = await this.#leerCarrito();
      const handle = this.dataset.handle;
      if (carrito && !carrito.items.some((/** @type {any} */ i) => i.handle === handle)) this.#intentar();
      return;
    }

    // Carrito parado: artículos y ≥ 3 min sin actividad con el cajón abierto o en la página de carrito.
    const cajonAbierto = Boolean(document.querySelector('#cart-drawer dialog[open]'));
    const enCarrito = this.dataset.plantilla === 'cart' || cajonAbierto;
    if (enCarrito && !this.#idleDisparado && Date.now() - this.#ultimaActividad >= 180_000) {
      this.#idleDisparado = true;
      const carrito = await this.#leerCarrito();
      if (carrito?.item_count > 0) this.#intentar();
    }
  }

  /** @returns {Promise<any | null>} */
  async #leerCarrito() {
    try {
      const respuesta = await fetch(`${raiz()}cart.js`, { headers: { Accept: 'application/json' } });
      return respuesta.ok ? await respuesta.json() : null;
    } catch {
      return null;
    }
  }

  /* ------------------------------------------------------------------ elegibilidad y elección */

  /** Aplica las reglas del momento (mínimo 20 s, una por sesión, nada abierto) y, si todo cuadra, la muestra. */
  #intentar() {
    if (this.#dialogo.open || this.#ofrecidaEnSesion()) return;
    if (this.#tiempoEnWeb() < 20_000) return;
    if (!this.#cumpleReglasFijas()) return;
    this.#mostrar(null);
  }

  /**
   * Elige la oferta según el contexto (spec § 4).
   * @param {'tarro' | 'general' | 'empujon' | 'forzar' | null} forzado
   * @returns {{ tipo: 'tarro' | 'general' | 'empujon', faltan?: number, pct?: string }}
   */
  #elegir(forzado) {
    if (forzado === 'tarro' || forzado === 'general') return { tipo: forzado };
    const tramos = /** @type {HTMLElement | null} */ (document.querySelector('[data-mb-tramos]'));
    const empujon = () => {
      if (!tramos || Number(tramos.dataset.pctActual) !== 0) return null;
      const faltan = Number(tramos.dataset.faltan);
      if (!(faltan > 0) || !tramos.dataset.lineaBarata) return null;
      return { tipo: /** @type {const} */ ('empujon'), faltan, pct: tramos.dataset.siguientePct };
    };

    if (forzado === 'empujon') return empujon() ?? { tipo: 'general' };
    if (this.dataset.tarro === 'true') return { tipo: 'tarro' };
    // En la ficha de algo que no es un tarro (una bolsa…) nunca se ofrece «su nombre»:
    // la foto y el texto hablarían de un producto que no se personaliza.
    if (this.dataset.plantilla === 'product') return empujon() ?? { tipo: 'general' };
    const tarrosEnCarrito = [...document.querySelectorAll('[data-mb-rescate-linea]')].some(
      (el) => Number(/** @type {HTMLElement} */ (el).dataset.tarros) > 0
    );
    if (tarrosEnCarrito) return { tipo: 'tarro' };
    return empujon() ?? { tipo: 'general' };
  }

  /* ------------------------------------------------------------------ ventana */

  /** @param {'tarro' | 'general' | 'empujon' | 'forzar' | null} forzado */
  #mostrar(forzado) {
    if (this.#dialogo.open) return;
    const oferta = this.#elegir(forzado);
    this.#tipo = oferta.tipo;
    this.#aceptado = false;
    this.#rellenar(oferta);

    if (!forzado) {
      guardarEstado({ visto: Date.now() });
      try {
        sessionStorage.setItem(CLAVE_SESION, '1');
      } catch {
        this.#dialogo.dataset.visto = '1';
      }
    }
    this.#dialogo.showModal();
    this.#emitir('visto');
  }

  /** @param {{ tipo: string, faltan?: number, pct?: string }} oferta */
  #rellenar(oferta) {
    const q = (/** @type {string} */ s) => /** @type {HTMLElement} */ (this.querySelector(s));
    let titulo;
    let texto;
    let boton;
    if (oferta.tipo === 'empujon') {
      const n = oferta.faltan ?? 1;
      titulo = `Te falta ${n} ${n === 1 ? 'bolsa' : 'bolsas'} para −${oferta.pct} %`;
      texto = 'Sin códigos: el descuento por cantidad se aplica solo en el carrito.';
      boton = 'Añadir otra bolsa';
    } else {
      ({ titulo, texto, boton } = TEXTOS[/** @type {'tarro' | 'general'} */ (oferta.tipo)]);
    }
    q('[data-titulo]').textContent = titulo;
    q('[data-texto]').textContent = texto;
    q('[data-aceptar]').textContent = boton;
    q('[data-aceptar]').removeAttribute('disabled');
    q('[data-aceptar]').removeAttribute('aria-busy');
    q('[data-error]').hidden = true;
    this.#dialogo.removeAttribute('data-estado');

    // El correo solo tiene sentido cuando hay un código que enviar; en escritorio se ve abierto.
    const correo = /** @type {HTMLDetailsElement} */ (q('[data-correo]'));
    correo.hidden = oferta.tipo === 'empujon';
    correo.open = matchMedia('(min-width: 750px)').matches;
    const foto = q('[data-foto]');
    if (foto.querySelector('img')) foto.hidden = false;
  }

  #alCerrar = () => {
    this.#dialogo.style.transform = '';
    if (!this.#aceptado) this.#emitir('cerrado');
  };

  /** @param {SubmitEvent} evento */
  #alAceptar = async (evento) => {
    evento.preventDefault();
    if (this.#ocupado) return;
    this.#ocupado = true;
    const boton = /** @type {HTMLButtonElement} */ (this.querySelector('[data-aceptar]'));
    const textoOriginal = boton.textContent;
    boton.disabled = true;
    boton.setAttribute('aria-busy', 'true');
    boton.textContent = TEXTOS.enviando;

    try {
      if (this.#tipo === 'empujon') {
        await this.#anadirBolsa();
        this.#terminar();
        if (this.dataset.plantilla === 'cart') location.reload();
        return;
      }
      const datos = await this.#pedirCodigo();
      guardarEstado({ codigo: datos.codigo, caduca: datos.caduca, tipoOferta: datos.tipo ?? this.#tipo, barraCerrada: false });
      this.#terminar();
      if (datos.aplicar_url && this.dataset.demo !== 'true') {
        location.assign(this.#urlDeAplicacion(datos.aplicar_url));
        return;
      }
      this.#pintarOferta();
    } catch (error) {
      console.warn('[mb-rescate]', error);
      boton.textContent = textoOriginal;
      const aviso = /** @type {HTMLElement} */ (this.querySelector('[data-error]'));
      aviso.textContent = this.#tipo === 'empujon' ? TEXTOS.errorBolsa : TEXTOS.error;
      aviso.hidden = false;
      this.#dialogo.dataset.estado = 'error';
    } finally {
      this.#ocupado = false;
      boton.removeAttribute('aria-busy');
    }
  };

  /** Cierra la ventana tras aceptar sin contarlo como "cerrado". */
  #terminar() {
    this.#aceptado = true;
    this.#emitir('aceptado');
    this.#dialogo.close();
  }

  /** @param {string} aplicar */
  #urlDeAplicacion(aplicar) {
    const destino = new URL(aplicar, location.origin);
    const vuelta = new URL(location.href);
    vuelta.searchParams.delete('rescate');
    destino.searchParams.set('redirect', vuelta.pathname + vuelta.search);
    return destino.toString();
  }

  /** @returns {Promise<{ codigo: string, caduca: string, tipo?: string, aplicar_url?: string }>} */
  async #pedirCodigo() {
    if (this.dataset.demo === 'true') {
      return { codigo: 'DEMO-AB12CD', caduca: new Date(Date.now() + VALIDEZ_MS).toISOString(), tipo: this.#tipo };
    }
    const endpoint = this.dataset.endpoint;
    if (!endpoint) throw new Error('Sin servicio configurado');

    const email = /** @type {HTMLInputElement} */ (this.querySelector('[data-email]'));
    const cuerpo = /** @type {Record<string, any>} */ ({
      tipo: this.#tipo,
      producto: this.dataset.handle ?? '',
      navegador_id: leerEstado().id,
      trampa: /** @type {HTMLInputElement} */ (this.querySelector('[data-trampa]')).value,
    });
    if (email.value.trim() && email.checkValidity()) {
      cuerpo.email = email.value.trim();
      cuerpo.marketing = /** @type {HTMLInputElement} */ (this.querySelector('[data-marketing]')).checked;
    }

    const controlador = new AbortController();
    const limite = setTimeout(() => controlador.abort(), 10_000);
    try {
      const respuesta = await fetch(endpoint, {
        method: 'POST',
        headers: JSON_CABECERAS,
        body: JSON.stringify(cuerpo),
        signal: controlador.signal,
      });
      const datos = await respuesta.json();
      if (!respuesta.ok || datos?.error || !datos?.codigo) throw new Error(datos?.error || `HTTP ${respuesta.status}`);
      return datos;
    } finally {
      clearTimeout(limite);
    }
  }

  /** Empujón: suma una unidad a la línea de granel más barata y avisa al cajón del carrito. */
  async #anadirBolsa() {
    const clave = /** @type {HTMLElement | null} */ (document.querySelector('[data-mb-tramos]'))?.dataset.lineaBarata;
    if (!clave) throw new Error('Sin línea de granel');
    const carrito = await this.#leerCarrito();
    const linea = carrito?.items.find((/** @type {any} */ i) => i.key === clave);
    if (!linea) throw new Error('La línea ya no está en el carrito');

    let eventos = null;
    try {
      eventos = await import('@shopify/events');
    } catch {
      /* sin eventos: el carrito se actualizará al recargar */
    }
    const diferido = eventos?.CartLinesUpdateEvent?.createPromise?.();
    if (diferido) {
      this.dispatchEvent(
        new eventos.CartLinesUpdateEvent({
          action: 'update',
          context: 'standard-action',
          lines: [{ id: clave, quantity: linea.quantity + 1 }],
          promise: diferido.promise,
        })
      );
      diferido.promise.catch(() => {});
    }

    try {
      const respuesta = await fetch(`${raiz()}cart/change.js`, {
        method: 'POST',
        headers: JSON_CABECERAS,
        body: JSON.stringify({ id: clave, quantity: linea.quantity + 1 }),
      });
      if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);
      const nuevo = await this.#leerCarrito();
      if (nuevo) {
        window.mbCarrito = nuevo;
        document.dispatchEvent(new CustomEvent('mb:carrito', { detail: nuevo }));
        diferido?.resolve({
          cart: eventos?.CartLinesUpdateEvent?.createCartFromAjaxResponse?.(nuevo),
          detail: {
            items: nuevo.items,
            itemCount: nuevo.item_count,
            source: 'mb-rescate',
            sourceId: 'mb-rescate',
            didError: false,
          },
        });
      }
    } catch (error) {
      diferido?.reject(error);
      throw error;
    }
  }

  /** @param {'visto' | 'aceptado' | 'cerrado'} accion */
  #emitir(accion) {
    const detail = { accion, tipo: this.#tipo };
    window.dispatchEvent(new CustomEvent('mb:rescate', { detail }));
    try {
      window.Shopify?.analytics?.publish?.(`rescate_${accion}`, { tipo: this.#tipo });
    } catch {
      /* la medición nunca debe romper la oferta */
    }
  }

  /** Móvil: deslizar la tarjeta hacia abajo la cierra. @param {AbortSignal} signal */
  #activarDeslizar(signal) {
    let inicio = 0;
    let delta = 0;
    let activo = false;
    const panel = /** @type {HTMLElement} */ (this.querySelector('.mb-rescate__panel'));
    const movil = () => matchMedia('(max-width: 749.98px)').matches;

    this.#dialogo.addEventListener(
      'touchstart',
      (e) => {
        activo = movil() && panel.scrollTop === 0;
        inicio = e.touches[0].clientY;
        delta = 0;
        this.#dialogo.style.transition = 'none';
      },
      { signal, passive: true }
    );
    this.#dialogo.addEventListener(
      'touchmove',
      (e) => {
        if (!activo) return;
        delta = Math.max(0, e.touches[0].clientY - inicio);
        this.#dialogo.style.transform = `translateY(${delta}px)`;
      },
      { signal, passive: true }
    );
    this.#dialogo.addEventListener(
      'touchend',
      () => {
        this.#dialogo.style.transition = '';
        if (activo && delta > 80) this.#dialogo.close();
        else this.#dialogo.style.transform = '';
        activo = false;
      },
      { signal, passive: true }
    );
  }

  /* ------------------------------------------------------------------ barra y cajón del carrito */

  /** Pinta la barra fina y la línea del cajón, y programa su retirada cuando caduque. */
  #pintarOferta() {
    this.#pintarBarra();
    this.#pintarLinea();
    clearTimeout(this.#temporizadorCaducidad);
    const { caduca } = leerEstado();
    const resto = caduca ? new Date(caduca).getTime() - Date.now() : 0;
    if (resto > 0 && resto < 2 ** 31 - 1) {
      // Se repinta cada minuto para que «quedan N min» sea real; al caducar, se retira.
      this.#temporizadorCaducidad = window.setTimeout(() => {
        this.#limpiarCaducado();
        this.#pintarOferta();
      }, Math.min(resto, 60_000));
    }
  }

  /**
   * Frase de la oferta. Solo dice "aplicado" si Shopify tiene el código aplicado en el carrito
   * (data-codigos del resumen del carrito); si no, explica cuándo se descontará. En modo prueba lo avisa.
   * @param {string} [codigosCarrito] códigos aplicables en el carrito, separados por comas
   * @returns {string}
   */
  #frase(codigosCarrito) {
    if (!this.#codigoVivo()) return '';
    const { caduca, tipoOferta, codigo } = leerEstado();
    const minutos = Math.max(1, Math.ceil((new Date(caduca).getTime() - Date.now()) / 60_000));
    const caducidad = `caduca ${formatearCaducidad(caduca)} (quedan ${minutos} min)`;
    if (this.dataset.demo === 'true') return `Modo prueba: el código ${codigo} es ficticio y no descuenta nada`;
    const aplicado = codigosCarrito !== undefined && codigosCarrito.split(',').includes(codigo);
    if (aplicado) {
      const base = tipoOferta === 'tarro' ? TEXTOS.tarro.barra : TEXTOS.general.barra;
      return `${base} · ${caducidad}`;
    }
    if (tipoOferta === 'tarro') return `Tu código ${codigo} descuenta el nombre al llevar un tarro «Con su nombre» · ${caducidad}`;
    return `Tu código ${codigo} (−5 %) se aplica en pedidos desde 15 € · ${caducidad}`;
  }

  #pintarBarra() {
    const barra = /** @type {HTMLElement} */ (document.querySelector('[data-barra]'));
    if (!barra) return;
    const frase = this.#frase();
    const visible = Boolean(frase) && !leerEstado().barraCerrada;
    const texto = /** @type {HTMLElement} */ (barra.querySelector('[data-barra-texto]'));
    if (visible && texto.textContent !== frase) texto.textContent = frase;
    barra.hidden = !visible;
  }

  #pintarLinea() {
    const frase = this.#frase();
    for (const linea of document.querySelectorAll('[data-mb-rescate-linea]')) {
      const fraseLinea = this.#frase(/** @type {HTMLElement} */ (linea).dataset.codigos ?? '') || frase;
      if (linea.textContent !== fraseLinea) linea.textContent = fraseLinea;
      /** @type {HTMLElement} */ (linea).hidden = !fraseLinea;
    }
  }
}

if (!customElements.get('mb-rescate')) customElements.define('mb-rescate', MbRescate);
